import { useState, useEffect, useMemo } from "react";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { useInvoices } from "../../contexts/InvoicesContext";
import { useExpenses } from "../../contexts/ExpensesContext";
import { getTaxConfig, TAX_CONFIGS, COUNTRIES } from "../../services/taxConfigService";
import { api } from "../../api/client";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(n, locale = "en-GB", currencyCode = "GBP") {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency", currency: currencyCode,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return n.toFixed(2);
  }
}

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str + "T12:00:00").toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function invoiceTax(lineItems = []) {
  return (lineItems || []).reduce(
    (s, r) => s + r.qty * r.unitPrice * (r.tax || 0) / 100, 0,
  );
}

function expenseTax(exp) {
  return Number(exp.amount) * (Number(exp.tax_rate) || 0) / 100;
}

// Build the period bucket that a date falls into, based on filing frequency.
function getPeriodKey(dateStr, filingPeriod) {
  const [y, m] = dateStr.split("-").map(Number);
  const mo = m - 1; // 0-indexed
  const pad = n => String(n).padStart(2, "0");

  if (filingPeriod === "monthly") {
    const lastDay = new Date(y, m, 0).getDate();
    return {
      key: `${y}-${pad(m)}`,
      label: new Date(y, mo, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
      start: `${y}-${pad(m)}-01`,
      end: `${y}-${pad(m)}-${lastDay}`,
    };
  }

  if (filingPeriod === "bimonthly") {
    const bi = Math.floor(mo / 2);
    const startMo0 = bi * 2;
    const endMo0 = startMo0 + 1;
    const lastDay = new Date(y, endMo0 + 1, 0).getDate();
    const labels = ["Jan–Feb", "Mar–Apr", "May–Jun", "Jul–Aug", "Sep–Oct", "Nov–Dec"];
    return {
      key: `${y}-B${bi + 1}`,
      label: `${labels[bi]} ${y}`,
      start: `${y}-${pad(startMo0 + 1)}-01`,
      end: `${y}-${pad(endMo0 + 1)}-${lastDay}`,
    };
  }

  // Default: calendar quarterly
  const q = Math.floor(mo / 3);
  const startMo0 = q * 3;
  const endMo0 = startMo0 + 2;
  const lastDay = new Date(y, endMo0 + 1, 0).getDate();
  return {
    key: `${y}-Q${q + 1}`,
    label: `Q${q + 1} ${y}`,
    start: `${y}-${pad(startMo0 + 1)}-01`,
    end: `${y}-${pad(endMo0 + 1)}-${lastDay}`,
  };
}

function buildPeriods(invoices, expenses, taxConfig) {
  const { filingPeriod, reclaimable } = taxConfig;
  const map = {};

  const ensure = p => {
    if (!map[p.key]) {
      map[p.key] = { ...p, outputTax: 0, inputTax: 0, taxableNet: 0, invoiceCount: 0, expenseCount: 0 };
    }
    return map[p.key];
  };

  invoices.forEach(inv => {
    if (!inv.issueDate || inv.status === "draft") return;
    const p = ensure(getPeriodKey(inv.issueDate, filingPeriod));
    p.outputTax += invoiceTax(inv.lineItems);
    p.taxableNet += (inv.lineItems || [])
      .filter(r => (r.tax || 0) > 0)
      .reduce((s, r) => s + r.qty * r.unitPrice, 0);
    p.invoiceCount += 1;
  });

  if (reclaimable) {
    expenses.forEach(exp => {
      if (!exp.date || (exp.tax_rate || 0) === 0) return;
      const p = ensure(getPeriodKey(exp.date, filingPeriod));
      p.inputTax += expenseTax(exp);
      p.expenseCount += 1;
    });
  }

  return Object.values(map).sort((a, b) => b.key.localeCompare(a.key));
}

function exportCSV(periods, taxConfig) {
  const { taxAbbr } = taxConfig;
  const fmt = n => n.toFixed(2);
  const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const headers = ["Period", "From", "To", `Taxable Net`, `Output ${taxAbbr}`,
    ...(taxConfig.reclaimable ? [`Input ${taxAbbr} (reclaimable)`] : []),
    `Net ${taxAbbr} Payable`, "Invoices"];
  const rows = [
    headers.map(esc).join(","),
    ...periods.map(p => {
      const net = p.outputTax - p.inputTax;
      return [
        p.label, p.start, p.end, fmt(p.taxableNet), fmt(p.outputTax),
        ...(taxConfig.reclaimable ? [fmt(p.inputTax)] : []),
        fmt(net), p.invoiceCount,
      ].map(esc).join(",");
    }),
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tax-summary-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const TH = { padding: "0.55rem 0.75rem", textAlign: "left", fontWeight: 700, fontSize: "0.78rem", color: "#fff", whiteSpace: "nowrap" };
const TD = { padding: "0.55rem 0.75rem", fontSize: "0.85rem", borderBottom: "1px solid #f0f0f0", verticalAlign: "middle" };

// ─── Period detail modal ──────────────────────────────────────────────────────

function PeriodDetailModal({ period, invoices, expenses, taxConfig, onClose }) {
  const { taxName, taxAbbr, reclaimable, locale, currencyCode } = taxConfig;
  const fmt = n => fmtMoney(n, locale, currencyCode);

  const pInvoices = invoices.filter(
    inv => inv.issueDate >= period.start && inv.issueDate <= period.end && inv.status !== "draft",
  );
  const pExpenses = reclaimable
    ? expenses.filter(exp => exp.date >= period.start && exp.date <= period.end && (exp.tax_rate || 0) > 0)
    : [];

  const statusPill = st => {
    const map = {
      paid: { bg: "#eaf6ef", color: "#1e6645", border: "#6abf85" },
      overdue: { bg: "#fde8e8", color: "#A12710", border: "#f5a5a5" },
      pending: { bg: "#fff8ee", color: "#7a4a10", border: "#FBC490" },
    };
    const s = map[st] || map.pending;
    return <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 6, padding: "0.15rem 0.55rem", fontSize: "0.72rem", fontWeight: 700 }}>{st}</span>;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content neutral" style={{ maxWidth: 780, width: "95vw", maxHeight: "90vh", overflowY: "auto", fontFamily: "Raleway, sans-serif" }}>
        <button className="close-button" onClick={onClose}>✕</button>
        <h3 style={{ fontFamily: "Poppins, sans-serif", marginBottom: "0.25rem" }}>
          {taxName} Detail — {period.label}
        </h3>
        <p style={{ fontSize: "0.82rem", color: "#888", marginTop: 0 }}>
          {fmtDate(period.start)} – {fmtDate(period.end)}
        </p>

        {/* Output tax */}
        <h4 style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.92rem", color: "#0C2D48", margin: "1rem 0 0.5rem" }}>
          Output {taxAbbr} — sales invoices
        </h4>
        {pInvoices.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: "0.85rem" }}>No invoices in this period.</p>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
              <thead>
                <tr style={{ background: "#0C2D48" }}>
                  {["Invoice", "Date", "Client", "Net", taxAbbr, "Gross", "Status"].map(h => (
                    <th key={h} style={{ ...TH, textAlign: h === "Invoice" || h === "Date" || h === "Client" || h === "Status" ? "left" : "right" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pInvoices.map(inv => {
                  const net = (inv.lineItems || []).reduce((s, r) => s + r.qty * r.unitPrice, 0);
                  const tax = invoiceTax(inv.lineItems);
                  return (
                    <tr key={inv.id}>
                      <td style={TD}>{inv.number}</td>
                      <td style={TD}>{fmtDate(inv.issueDate)}</td>
                      <td style={TD}>{inv.contactName || "—"}</td>
                      <td style={{ ...TD, textAlign: "right" }}>{fmt(net)}</td>
                      <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: tax > 0 ? "#0C2D48" : "#ccc" }}>{fmt(tax)}</td>
                      <td style={{ ...TD, textAlign: "right", fontWeight: 700 }}>{fmt(net + tax)}</td>
                      <td style={TD}>{statusPill(inv.status)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f8f9fb", fontWeight: 700 }}>
                  <td style={TD} colSpan={3}>Totals</td>
                  <td style={{ ...TD, textAlign: "right" }}>
                    {fmt(pInvoices.reduce((s, inv) => s + (inv.lineItems || []).reduce((a, r) => a + r.qty * r.unitPrice, 0), 0))}
                  </td>
                  <td style={{ ...TD, textAlign: "right", color: "#0C2D48" }}>
                    {fmt(pInvoices.reduce((s, inv) => s + invoiceTax(inv.lineItems), 0))}
                  </td>
                  <td style={{ ...TD, textAlign: "right" }}>
                    {fmt(pInvoices.reduce((s, inv) => {
                      const net = (inv.lineItems || []).reduce((a, r) => a + r.qty * r.unitPrice, 0);
                      return s + net + invoiceTax(inv.lineItems);
                    }, 0))}
                  </td>
                  <td style={TD} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Input tax — only for reclaimable jurisdictions */}
        {reclaimable && (
          <>
            <h4 style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.92rem", color: "#0C2D48", margin: "1.5rem 0 0.5rem" }}>
              Input {taxAbbr} — reclaimable expenses
            </h4>
            {pExpenses.length === 0 ? (
              <p style={{ color: "#aaa", fontSize: "0.85rem" }}>No {taxAbbr}-bearing expenses in this period.</p>
            ) : (
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 440 }}>
                  <thead>
                    <tr style={{ background: "#0C2D48" }}>
                      {["Date", "Description", "Category", "Net", "Rate", taxAbbr].map(h => (
                        <th key={h} style={{ ...TH, textAlign: h === "Date" || h === "Description" || h === "Category" ? "left" : "right" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pExpenses.map(exp => (
                      <tr key={exp.id}>
                        <td style={TD}>{fmtDate(exp.date)}</td>
                        <td style={TD}>{exp.title || "—"}</td>
                        <td style={TD}>{exp.category || "—"}</td>
                        <td style={{ ...TD, textAlign: "right" }}>{fmt(Number(exp.amount))}</td>
                        <td style={{ ...TD, textAlign: "right" }}>{exp.tax_rate || 0}%</td>
                        <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: "#1e6645" }}>{fmt(expenseTax(exp))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#f8f9fb", fontWeight: 700 }}>
                      <td style={TD} colSpan={5}>Total input {taxAbbr}</td>
                      <td style={{ ...TD, textAlign: "right", color: "#1e6645" }}>
                        {fmt(pExpenses.reduce((s, e) => s + expenseTax(e), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}

        {/* Net payable strip */}
        <div style={{
          marginTop: "1.5rem", padding: "1rem 1.25rem",
          background: "#0C2D48", borderRadius: 10, color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
        }}>
          <div>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#82B3CC" }}>
              {reclaimable ? `Net ${taxAbbr} payable` : `${taxAbbr} collected`}
            </div>
            {reclaimable && (
              <div style={{ fontSize: "0.78rem", color: "#82B3CC", marginTop: "0.2rem" }}>
                Output {fmt(period.outputTax)} − Input {fmt(period.inputTax)}
              </div>
            )}
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 900, color: (period.outputTax - period.inputTax) >= 0 ? "#E4E69B" : "#6abf85" }}>
            {fmt(period.outputTax - period.inputTax)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Source badge ─────────────────────────────────────────────────────────────

const SOURCE_LABELS = {
  manual: { text: "Manually set", bg: "#e8f4fd", color: "#1a6fa8" },
  profile: { text: "From profile", bg: "#eaf6ef", color: "#1e6645" },
  timezone: { text: "Auto-detected (timezone)", bg: "#fff8ee", color: "#7a4a10" },
  language: { text: "Auto-detected (language)", bg: "#fff8ee", color: "#7a4a10" },
  default: { text: "Default", bg: "#f0f0f0", color: "#888" },
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function TaxSummary() {
  const { currentUser } = useCurrentUser();

  // Country override: persisted to user preferences so it survives refresh
  const [countryOverride, setCountryOverride] = useState("");

  // VAT-registered toggle — only relevant for UK
  const [vatRegistered, setVatRegistered] = useState(true);

  useEffect(() => {
    api('/api/users/me')
      .then(res => res.json())
      .then(data => {
        const prefs = data?.user?.preferences ?? {};
        if (prefs.taxConfigCountry) setCountryOverride(prefs.taxConfigCountry);
        if (prefs.taxVatRegistered === false) setVatRegistered(false);
      })
      .catch(() => { });
  }, []);

  function applyCountryOverride(code) {
    setCountryOverride(code);
    api('/api/users/me', { method: 'PUT', body: JSON.stringify({ preferences: { taxConfigCountry: code || null } }) })
      .catch(err => console.error('Failed to save country override:', err));
  }

  const taxConfig = useMemo(() => getTaxConfig(currentUser, countryOverride || undefined), [currentUser, countryOverride]);

  const { taxName, taxAbbr, reclaimable, locale, currencyCode, source, countryCode } = taxConfig;
  const fmt = n => fmtMoney(n, locale, currencyCode);

  const { invoices: allInvoices } = useInvoices();
  const { expenses: allExpenses } = useExpenses();

  const [yearsBack, setYearsBack] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  function toggleVatRegistered(val) {
    setVatRegistered(val);
    api('/api/users/me', { method: 'PUT', body: JSON.stringify({ preferences: { taxVatRegistered: val } }) })
      .catch(err => console.error('Failed to save VAT preference:', err));
  }

  const allPeriods = useMemo(
    () => buildPeriods(allInvoices, allExpenses, taxConfig),
    [allInvoices, allExpenses, taxConfig],
  );

  const cutoff = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - yearsBack);
    return d.toISOString().slice(0, 10);
  }, [yearsBack]);

  const visible = allPeriods.filter(p => p.end >= cutoff);

  const totalOutput = visible.reduce((s, p) => s + p.outputTax, 0);
  const totalInput = visible.reduce((s, p) => s + p.inputTax, 0);
  const totalNet = totalOutput - totalInput;

  const srcBadge = SOURCE_LABELS[source] || SOURCE_LABELS.default;
  const countryName = COUNTRIES.find(c => c.code === countryCode)?.name;

  return (
    <div style={{ padding: "1rem", maxWidth: 1000, margin: "0 auto", fontFamily: "Raleway, sans-serif" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.4rem", fontFamily: "Poppins, sans-serif" }}>Tax Summary</h2>
          <div style={{ fontSize: "0.82rem", color: "#aaa", marginTop: "0.15rem" }}>
            {taxName} filing periods — output vs input {taxAbbr}
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={yearsBack}
            onChange={e => setYearsBack(Number(e.target.value))}
            style={{ padding: "0.45rem 0.75rem", borderRadius: 7, border: "1px solid #ddd", fontSize: "0.85rem", fontFamily: "Raleway, sans-serif", background: "#fff" }}
          >
            <option value={1}>Last 12 months</option>
            <option value={2}>Last 2 years</option>
            <option value={3}>Last 3 years</option>
            <option value={5}>Last 5 years</option>
          </select>
          <button
            onClick={() => exportCSV(visible, taxConfig)}
            style={{ padding: "0.45rem 1rem", background: "#E4E69B", border: "none", borderRadius: 7, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", color: "#0C2D48", fontFamily: "Raleway, sans-serif" }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Jurisdiction bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
        padding: "0.65rem 1rem", background: "#f8f9fb", border: "1px solid #e5e7eb",
        borderRadius: 8, marginBottom: "1.25rem",
      }}>
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#555" }}>Tax jurisdiction:</span>
        <select
          value={countryOverride || countryCode || ""}
          onChange={e => applyCountryOverride(e.target.value)}
          style={{ padding: "0.3rem 0.6rem", borderRadius: 6, border: "1px solid #ddd", fontSize: "0.85rem", fontFamily: "Raleway, sans-serif", background: "#fff" }}
        >
          <option value="">— Auto-detect —</option>
          {COUNTRIES.map(c => (
            <option key={c.code} value={c.code}>{c.name} ({TAX_CONFIGS[c.code]?.taxAbbr || c.code})</option>
          ))}
        </select>
        <span style={{ background: srcBadge.bg, color: srcBadge.color, border: `1px solid ${srcBadge.color}22`, borderRadius: 6, padding: "0.15rem 0.55rem", fontSize: "0.72rem", fontWeight: 700 }}>
          {srcBadge.text}{countryName ? ` · ${countryName}` : ""}
        </span>
        {taxConfig.thresholdNote && (
          <span style={{ fontSize: "0.75rem", color: "#aaa", marginLeft: "auto" }}>{taxConfig.thresholdNote}</span>
        )}
      </div>

      {/* VAT registered toggle — UK only */}
      {countryCode === "GB" && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", padding: "0.65rem 1rem", background: "#fff", border: "1px solid #35454C", borderRadius: 8, marginBottom: "1.25rem" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.85rem", fontWeight: 700, color: "#0C2D48" }}>VAT registered</div>
            <div style={{ fontFamily: "Raleway, sans-serif", fontSize: "0.78rem", color: "#4a5a5b", marginTop: "0.1rem" }}>
              Businesses below the VAT threshold (currently £90,000) do not need to register for VAT.
            </div>
          </div>
          <button
            role="switch"
            aria-checked={vatRegistered}
            onClick={() => toggleVatRegistered(!vatRegistered)}
            style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", background: vatRegistered ? "#0C2D48" : "#c5cdd4", position: "relative", transition: "background 0.2s", flexShrink: 0 }}
          >
            <span style={{ position: "absolute", top: 4, left: vatRegistered ? 23 : 4, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", display: "block" }} />
          </button>
        </div>
      )}

      {/* Not VAT registered message */}
      {countryCode === "GB" && !vatRegistered && (
        <div style={{ padding: "1.5rem", background: "#fff", border: "1px solid #c5cdd4", borderRadius: 12, textAlign: "center", marginBottom: "1.25rem" }}>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "1rem", fontWeight: 700, color: "#0C2D48", marginBottom: "0.5rem" }}>Not VAT registered</div>
          <div style={{ fontFamily: "Raleway, sans-serif", fontSize: "0.88rem", color: "#4a5a5b", lineHeight: 1.6 }}>
            You do not need to charge or account for VAT. If your taxable turnover exceeds £90,000 in any 12-month period, you must register for VAT.
          </div>
        </div>
      )}

      {/* Summary cards + table — only shown when VAT registered (or non-UK) */}
      {(countryCode !== "GB" || vatRegistered) && (<>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
          {[
            { label: `Total output ${taxAbbr}`, value: fmt(totalOutput), sub: `${taxAbbr} charged to clients`, color: "#0C2D48" },
            ...(reclaimable ? [{ label: `Total input ${taxAbbr}`, value: fmt(totalInput), sub: `${taxAbbr} reclaimable on expenses`, color: "#1e6645" }] : []),
            { label: reclaimable ? `Net ${taxAbbr} payable` : `${taxAbbr} collected`, value: fmt(totalNet), sub: reclaimable ? "Output minus input" : "To remit to tax authority", color: totalNet >= 0 ? "#A12710" : "#1e6645" },
          ].map(card => (
            <div key={card.label} style={{
              flex: "1 1 180px", minWidth: 160, padding: "1rem 1.1rem",
              background: "#fff", border: "1px solid #35454C", borderRadius: 12,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em" }}>{card.label}</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 900, color: card.color, marginTop: "0.25rem", lineHeight: 1.15 }}>{card.value}</div>
              <div style={{ fontSize: "0.74rem", color: "#aaa", marginTop: "0.1rem" }}>{card.sub}</div>
            </div>
          ))}
        </div>

        {/* Periods table */}
        {visible.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #35454C", borderRadius: 12, padding: "2.5rem", textAlign: "center", color: "#aaa" }}>
            No invoices or {taxAbbr}-bearing expenses found in this period.
          </div>
        ) : (
          <div style={{ background: "#fff", border: "1px solid #35454C", borderRadius: 12, overflow: "hidden", marginBottom: "1rem" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: reclaimable ? 580 : 460 }}>
                <thead>
                  <tr style={{ background: "#0C2D48" }}>
                    <th style={TH}>Period</th>
                    <th style={{ ...TH, textAlign: "right" }}>Taxable net</th>
                    <th style={{ ...TH, textAlign: "right" }}>Output {taxAbbr}</th>
                    {reclaimable && <th style={{ ...TH, textAlign: "right" }}>Input {taxAbbr}</th>}
                    <th style={{ ...TH, textAlign: "right" }}>Net payable</th>
                    <th style={{ ...TH, textAlign: "right" }}>Invoices</th>
                    <th style={TH} />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p, idx) => {
                    const net = p.outputTax - p.inputTax;
                    return (
                      <tr key={p.key} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={{ ...TD, fontWeight: 700, color: "#0C2D48" }}>
                          {p.label}
                          <div style={{ fontSize: "0.72rem", color: "#aaa", fontWeight: 400 }}>
                            {fmtDate(p.start)} – {fmtDate(p.end)}
                          </div>
                        </td>
                        <td style={{ ...TD, textAlign: "right" }}>{fmt(p.taxableNet)}</td>
                        <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: "#0C2D48" }}>{fmt(p.outputTax)}</td>
                        {reclaimable && <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: "#1e6645" }}>{fmt(p.inputTax)}</td>}
                        <td style={{ ...TD, textAlign: "right", fontWeight: 900, color: net >= 0 ? "#A12710" : "#1e6645" }}>{fmt(net)}</td>
                        <td style={{ ...TD, textAlign: "right", color: "#888" }}>{p.invoiceCount}</td>
                        <td style={{ ...TD, textAlign: "right" }}>
                          {(p.outputTax > 0 || p.inputTax > 0) && (
                            <button
                              onClick={() => setSelectedPeriod(p)}
                              style={{ padding: "0.2rem 0.65rem", background: "transparent", border: "1px solid #35454C", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", color: "#0C2D48", fontFamily: "Raleway, sans-serif" }}
                            >
                              Details
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#0C2D48", color: "#fff" }}>
                    <td style={{ ...TD, fontWeight: 700, color: "#fff", borderBottom: "none" }}>Totals</td>
                    <td style={{ ...TD, textAlign: "right", color: "#82B3CC", borderBottom: "none" }}>
                      {fmt(visible.reduce((s, p) => s + p.taxableNet, 0))}
                    </td>
                    <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: "#E4E69B", borderBottom: "none" }}>{fmt(totalOutput)}</td>
                    {reclaimable && <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: "#6abf85", borderBottom: "none" }}>{fmt(totalInput)}</td>}
                    <td style={{ ...TD, textAlign: "right", fontWeight: 900, color: totalNet >= 0 ? "#FBC490" : "#6abf85", borderBottom: "none" }}>{fmt(totalNet)}</td>
                    <td style={{ ...TD, textAlign: "right", color: "#82B3CC", borderBottom: "none" }}>
                      {visible.reduce((s, p) => s + p.invoiceCount, 0)}
                    </td>
                    <td style={{ ...TD, borderBottom: "none" }} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {taxConfig.disclaimer && (
          <p style={{ fontSize: "0.75rem", color: "#aaa", lineHeight: 1.6, margin: "0.5rem 0 0" }}>
            {taxConfig.disclaimer}
          </p>
        )}
      </>)} {/* end VAT-registered conditional */}

      {/* Detail modal */}
      {selectedPeriod && (
        <PeriodDetailModal
          period={selectedPeriod}
          invoices={allInvoices}
          expenses={allExpenses}
          taxConfig={taxConfig}
          onClose={() => setSelectedPeriod(null)}
        />
      )}
    </div>
  );
}
